import { VideoErrorComponent } from './components/video-error/video-error.component'
import { VideoScrubberControlComponent } from './components/video-scrubber-control/video-scrubber-control.component'
import { LiveRoutingModule } from './live-routing.module'
import { LiveComponent } from './live.component'
import { VideoAdsComponent } from './components/video-ads.component'
import { SharedModule } from '../shared/shared.module'
import { PaywallModule } from '../shared/paywall/paywall.module'
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core'
import { AngularDraggableModule } from 'angular2-draggable'
import { VideoPlayerControlsComponent } from './components/video-player-controls/video-player-controls.component'
import { StreamTileComponent } from './components/stream-tile/stream-tile.component'
import { VideoQualityControlComponent } from './components/video-quality-control/video-quality-control.component'
import { VideoVolumeControlComponent } from './components/video-volume-control/video-volume-control.component'
import { MultiCamWidgetComponent } from './components/multi-cam-widget/multi-cam-widget.component'
import { PlatformService } from '../singleton-services/platform.service'
import { AuthService } from '../singleton-services/auth/auth.service'
import { LiveEventService } from './services/event.service'
import { VideoEventLoggingService } from './services/video-event-logging.service'
import { livePlayerEventsLoggingFactory } from './factories/live-logging.factory'
import {
  hlsLoaderFactory,
  HLS_LOADER_INIT_DELAY_MAX,
  HLS_LOADER_INIT_DELAY_MIN,
  HLS_LOADER_RETRY_INITIAL_INTERVAL,
  HLS_LOADER_RETRY_MAX_ATTEMPTS,
  HLS_LOADER_INIT_DELAY_FN,
  DEFAULT_HLS_LOADER_INIT_DELAY_FN
} from './factories/live-hls-loader.factory'
import { TabsComponent } from './components/tabs/tabs.component'
import { TabComponent } from './components/tabs/tab.component'
import { livePlayerHeartbeatFactory } from './factories/live-heartbeat.factory'
import { MAX_VID_PREVIEW_SECS, PREVIEW_STORE_KEY, PreviewService } from './services/preview.service'
import { HEARTBEAT_MILLI_SECONDS, HeartbeatService } from './services/heartbeat.service'
import { AnalyticsModule } from '../shared/analytics/analytics.module'
import { VideoAdsService } from './services/video-ads.service'
import { liveTeardownFactory } from './factories/live-teardown.factory'
import { EnvironmentService } from '../singleton-services/environment.service'
import { FloLiveDateTimePipe } from './components/live-date-time.pipe'
import { UnMuteButtonComponent } from './components/unmute-button/unmute-button.component'
import { maybe } from 'typescript-monads'
import { EventAddToCalendarModule } from '../shared/event-add-to-calendar/event-add-to-calendar.module'
import { UncheckOnDocumentClickModule } from '../shared/uncheck-on-document-click/uncheck-on-document-click.module'
import { FloGridListModule } from '@flosportsinc/ng-grid-list'
import { StreamTileSportDataComponent } from './components/stream-tile/stream-tile-sport-data/stream-tile-sport-data.component'
import { StreamTileThumbnailComponent } from './components/stream-tile/stream-tile-thumbnail/stream-tile-thumbnail.component'
import { VideoPlayerComponent } from './components/video-player/video-player.component'
import { uuidPerTabGenFn } from './components/video-player/video-player.interfaces'
import {
  VIDEO_PLAYER_EVENT_UUID_TAB_GENERATOR,
  VIDEO_PLAYER_HLS_URL_LOADER,
  VIDEO_PLAYER_EVENT_BINDINGS
} from './components/video-player/video-player.tokens'
import { v4 } from 'uuid'
import { KeyboardControlsModule } from '../shared/keyboard-controls/keyboard-controls.module'
import { StreamTileNoSportDataComponent } from './components/stream-tile/stream-tile-no-sport-data/stream-tile-no-sport-data.component'
import { WINDOW } from '../app.injections'
import { LiveLikeComponent } from './components/livelike/live-like.component'
import { RaceMonitorComponent } from './components/racemonitor/race-monitor.component'
import { FloSanitizeResourceUrlModule } from '../libs/_pipes/sanitize-url/sanitize-resource-url.pipe.module'
import { HtmlViewModule } from '../libs/html-view/html-view.module'
import { LiveAnalyticsService } from './live-analytics.service'

export function defaultTabIdGenerator(ps: PlatformService): uuidPerTabGenFn {
  const lambda = () => {
    if (ps.isServer || typeof sessionStorage === 'undefined') {
      return ''
    }

    const SHARED_WIN_KEY = 'FLO-VIDEO-EVTS-TAB-ID'
    const fetchKey = () => sessionStorage.getItem(SHARED_WIN_KEY)

    return maybe(fetchKey()).valueOrCompute(() => {
      sessionStorage.setItem(SHARED_WIN_KEY, v4())
      return fetchKey() as string
    })
  }
  return lambda
}

@NgModule({
  imports: [
    SharedModule,
    LiveRoutingModule,
    AngularDraggableModule,
    PaywallModule,
    AnalyticsModule,
    EventAddToCalendarModule,
    UncheckOnDocumentClickModule,
    FloGridListModule.config({
      selectNextEmptyOnCount: true,
      selectNextEmptyOnAdd: true
    }),
    KeyboardControlsModule,
    FloSanitizeResourceUrlModule,
    HtmlViewModule
  ],
  declarations: [
    LiveComponent,
    MultiCamWidgetComponent,
    StreamTileComponent,
    TabComponent,
    TabsComponent,
    VideoAdsComponent,
    VideoErrorComponent,
    VideoPlayerComponent,
    VideoPlayerControlsComponent,
    VideoQualityControlComponent,
    VideoScrubberControlComponent,
    VideoVolumeControlComponent,
    FloLiveDateTimePipe,
    UnMuteButtonComponent,
    StreamTileSportDataComponent,
    StreamTileNoSportDataComponent,
    StreamTileThumbnailComponent,
    LiveLikeComponent,
    RaceMonitorComponent
  ],
  exports: [
    LiveComponent,
    StreamTileComponent,
    VideoPlayerComponent,
    VideoPlayerControlsComponent,
    VideoQualityControlComponent,
    VideoVolumeControlComponent,
    VideoScrubberControlComponent,
    MultiCamWidgetComponent,
    TabsComponent,
    TabComponent,
    VideoErrorComponent,
    StreamTileSportDataComponent,
    StreamTileNoSportDataComponent,
    StreamTileThumbnailComponent,
    LiveLikeComponent,
    RaceMonitorComponent
  ],
  providers: [
    LiveEventService,
    PreviewService,
    VideoEventLoggingService,
    VideoAdsService,
    HeartbeatService,
    { provide: PREVIEW_STORE_KEY, useValue: 'vp' },
    { provide: MAX_VID_PREVIEW_SECS, useValue: 60 },
    { provide: HEARTBEAT_MILLI_SECONDS, useValue: 60000 },
    { provide: HLS_LOADER_INIT_DELAY_MIN, useValue: 0 },
    { provide: HLS_LOADER_INIT_DELAY_MAX, useValue: 200 },
    { provide: HLS_LOADER_RETRY_INITIAL_INTERVAL, useValue: 1000 },
    { provide: HLS_LOADER_RETRY_MAX_ATTEMPTS, useValue: 3 },
    { provide: VIDEO_PLAYER_EVENT_UUID_TAB_GENERATOR, useFactory: defaultTabIdGenerator, deps: [PlatformService] },
    { provide: HLS_LOADER_INIT_DELAY_FN, useFactory: DEFAULT_HLS_LOADER_INIT_DELAY_FN },
    {
      provide: VIDEO_PLAYER_HLS_URL_LOADER,
      useFactory: hlsLoaderFactory,
      deps: [
        LiveEventService,
        PlatformService,
        AuthService,
        PreviewService,
        HLS_LOADER_INIT_DELAY_FN,
        HLS_LOADER_INIT_DELAY_MIN,
        HLS_LOADER_INIT_DELAY_MAX,
        HLS_LOADER_RETRY_INITIAL_INTERVAL,
        HLS_LOADER_RETRY_MAX_ATTEMPTS
      ]
    },
    {
      provide: VIDEO_PLAYER_EVENT_BINDINGS,
      useFactory: livePlayerEventsLoggingFactory,
      deps: [VideoEventLoggingService, WINDOW, EnvironmentService],
      multi: true
    },
    {
      provide: VIDEO_PLAYER_EVENT_BINDINGS,
      useFactory: livePlayerHeartbeatFactory,
      deps: [HeartbeatService],
      multi: true
    },
    {
      provide: VIDEO_PLAYER_EVENT_BINDINGS,
      useFactory: liveTeardownFactory,
      deps: [LiveEventService],
      multi: true
    },
    LiveAnalyticsService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LiveModule {}
